/**
 * Klang ohne Fremddateien: Jede Ansage wird aus einfachen Schwingungen und Rauschen erzeugt.
 * Wer eigene Aufnahmen nutzen möchte, legt sie als `/sounds/<CUE>.mp3` ab – sie haben dann Vorrang.
 */
export type Cue =
  | 'DAY'
  | 'NIGHT'
  | 'ROLE_CALL'
  | 'MORNING'
  | 'VOTING'
  | 'VOTE_REVEAL'
  | 'DEATH'
  | 'MAYOR_ELECTION'
  | 'DRUNK'
  | 'VICTORY'
  | 'WIN_VILLAGE'
  | 'WIN_VAMPIRE'
  | 'WIN_LOVERS'
  | 'WIN_FOOL';

export const CUE_LABELS: Record<Cue, string> = {
  DAY: 'Der Tag bricht an',
  NIGHT: 'Nacht bricht herein',
  ROLE_CALL: 'Rolle erwacht',
  MORNING: 'Morgen graut',
  VOTING: 'Abstimmung beginnt',
  VOTE_REVEAL: 'Stimmen werden gezeigt',
  DEATH: 'Totenglocke',
  MAYOR_ELECTION: 'Bürgermeisterwahl',
  DRUNK: 'Der Trunkenbold erwacht',
  VICTORY: 'Die Partie endet',
  WIN_VILLAGE: 'Siegesmelodie: Dorf',
  WIN_VAMPIRE: 'Siegesmelodie: Vampire',
  WIN_LOVERS: 'Siegesmelodie: Geliebte',
  WIN_FOOL: 'Siegesmelodie: Narr',
};

/** Welche Melodie zu welchem Sieg gehört. */
export const WIN_CUES: Record<string, Cue> = {
  VILLAGE: 'WIN_VILLAGE',
  VAMPIRE: 'WIN_VAMPIRE',
  LOVERS: 'WIN_LOVERS',
  FOOL: 'WIN_FOOL',
};

interface Recipe {
  /** Grundtöne in Hertz. */
  tones: number[];
  duration: number;
  type: OscillatorType;
  /** Anteil Rauschen (Wind, Atem) von 0 bis 1. */
  noise: number;
  gain: number;
}

const RECIPES: Record<Cue, Recipe> = {
  DAY: { tones: [261.6, 329.6, 392], duration: 2.0, type: 'triangle', noise: 0.04, gain: 0.3 },
  NIGHT: { tones: [98, 73.4], duration: 2.6, type: 'sine', noise: 0.35, gain: 0.5 },
  ROLE_CALL: { tones: [196, 261.6], duration: 1.1, type: 'triangle', noise: 0.08, gain: 0.35 },
  MORNING: { tones: [329.6, 392, 493.9], duration: 2.2, type: 'sine', noise: 0.05, gain: 0.3 },
  VOTING: { tones: [146.8, 220], duration: 1.0, type: 'square', noise: 0.02, gain: 0.16 },
  VOTE_REVEAL: { tones: [174.6, 233.1, 349.2], duration: 1.6, type: 'triangle', noise: 0.04, gain: 0.28 },
  DEATH: { tones: [65.4, 98], duration: 3.2, type: 'sine', noise: 0.2, gain: 0.55 },
  MAYOR_ELECTION: { tones: [261.6, 392, 523.3], duration: 1.8, type: 'triangle', noise: 0.03, gain: 0.3 },
  DRUNK: { tones: [110, 155.6], duration: 1.8, type: 'sawtooth', noise: 0.15, gain: 0.22 },
  VICTORY: { tones: [196, 293.7, 392, 587.3], duration: 3.4, type: 'triangle', noise: 0.03, gain: 0.34 },
  // Siegesmelodien: bewusst schlicht gehalten – gedacht als Platzhalter für eigene Aufnahmen.
  WIN_VILLAGE: { tones: [261.6, 329.6, 392, 523.3], duration: 4.0, type: 'triangle', noise: 0.04, gain: 0.34 },
  WIN_VAMPIRE: { tones: [146.8, 174.6, 233.1, 293.7], duration: 4.2, type: 'sawtooth', noise: 0.12, gain: 0.3 },
  WIN_LOVERS: { tones: [349.2, 440, 523.3, 659.3], duration: 4.0, type: 'sine', noise: 0.02, gain: 0.32 },
  WIN_FOOL: { tones: [392, 466.2, 311.1, 587.3, 349.2], duration: 4.2, type: 'square', noise: 0.03, gain: 0.2 },
};

/** Einstellungen überleben das Neuladen der Seite. */
function readStored(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* privates Fenster: dann gilt die Einstellung nur für diese Sitzung */
  }
}

export class SoundBoard {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private files = new Map<Cue, AudioBuffer | null>();
  private _volume = readStored('noxbury:volume', 0.7);
  private _muted = readStored('noxbury:muted', 0) === 1;

  get volume(): number {
    return this._volume;
  }

  get muted(): boolean {
    return this._muted;
  }

  /** Browser erlauben Klang erst nach einer Nutzeraktion – daher beim ersten Klick aufwecken. */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this._muted ? 0 : this._volume;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setVolume(v: number): void {
    this._volume = Math.min(1, Math.max(0, v));
    writeStored('noxbury:volume', this._volume);
    this.applyGain();
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    writeStored('noxbury:muted', muted ? 1 : 0);
    this.applyGain();
  }

  /** Sanft nachregeln, damit es beim Ziehen des Reglers nicht knackst. */
  private applyGain(): void {
    if (!this.master || !this.ctx) return;
    const target = this._muted ? 0 : this._volume;
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.02);
  }

  private async loadFile(cue: Cue): Promise<AudioBuffer | null> {
    if (this.files.has(cue)) return this.files.get(cue) ?? null;
    let buffer: AudioBuffer | null = null;
    try {
      const res = await fetch(`/sounds/${cue}.mp3`);
      if (res.ok && this.ctx) buffer = await this.ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      buffer = null;
    }
    this.files.set(cue, buffer);
    return buffer;
  }

  async play(cue: Cue): Promise<void> {
    await this.unlock();
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || this._muted) return;

    const file = await this.loadFile(cue);
    if (file) {
      const src = ctx.createBufferSource();
      src.buffer = file;
      src.connect(master);
      src.start();
      return;
    }

    const recipe = RECIPES[cue];
    const now = ctx.currentTime;
    const bus = ctx.createGain();
    bus.gain.value = recipe.gain;
    bus.connect(master);

    recipe.tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      const start = now + i * (recipe.duration / (recipe.tones.length + 1));
      const length = recipe.duration - i * 0.12;
      osc.type = recipe.type;
      osc.frequency.setValueAtTime(freq, start);
      env.gain.setValueAtTime(0.0001, start);
      env.gain.exponentialRampToValueAtTime(0.9, start + 0.08);
      env.gain.exponentialRampToValueAtTime(0.0001, start + length);
      osc.connect(env).connect(bus);
      osc.start(start);
      osc.stop(start + length + 0.05);
    });

    if (recipe.noise > 0) {
      const frames = Math.floor(ctx.sampleRate * recipe.duration);
      const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
      const src = ctx.createBufferSource();
      const filter = ctx.createBiquadFilter();
      const env = ctx.createGain();
      src.buffer = buffer;
      filter.type = 'lowpass';
      filter.frequency.value = 700;
      env.gain.value = recipe.noise;
      src.connect(filter).connect(env).connect(bus);
      src.start(now);
    }
  }
}

/**
 * Hintergrundklang für Tag und Nacht. Beide Spuren laufen in Schleife; beim Wechsel wird die eine
 * aus- und die andere eingeblendet, damit kein harter Schnitt entsteht. Fehlt eine Datei, bleibt
 * es einfach still – die Überblendung des Bildes läuft davon unabhängig weiter.
 */
export class Ambience {
  private tracks = new Map<string, HTMLAudioElement>();
  private current: string | null = null;
  private timers = new Set<ReturnType<typeof setInterval>>();
  private _volume = readStored('noxbury:music', 0.35);

  get volume(): number {
    return this._volume;
  }

  setVolume(value: number, remember = true): void {
    this._volume = Math.min(1, Math.max(0, value));
    if (remember) writeStored('noxbury:music', this._volume);
    const track = this.current ? this.tracks.get(this.current) : null;
    // Läuft gerade eine Überblendung, zieht sie am Ende ohnehin auf den neuen Wert nach.
    if (track) track.volume = this._volume;
  }

  /** Wechselt die Spur mit einer Überblendung über `durationMs`. */
  crossfadeTo(src: string | null, durationMs: number): void {
    if (this.current === src) return;
    const from = this.current ? this.tracks.get(this.current) : null;
    const to = src ? this.track(src) : null;
    this.current = src;

    if (to) {
      to.volume = 0;
      void to.play().catch(() => undefined); // Ohne Berührung der Seite verweigert der Browser – dann bleibt es still.
    }

    const steps = Math.max(1, Math.round(durationMs / 50));
    let step = 0;
    const timer = setInterval(() => {
      step += 1;
      const ratio = Math.min(1, step / steps);
      if (from) from.volume = this._volume * (1 - ratio);
      if (to) to.volume = this._volume * ratio;
      if (ratio >= 1) {
        clearInterval(timer);
        this.timers.delete(timer);
        if (from) {
          from.pause();
          from.currentTime = 0;
        }
      }
    }, 50);
    this.timers.add(timer);
  }

  stop(): void {
    for (const timer of this.timers) clearInterval(timer);
    this.timers.clear();
    for (const track of this.tracks.values()) {
      track.pause();
      track.currentTime = 0;
    }
    this.current = null;
  }

  private track(src: string): HTMLAudioElement {
    let track = this.tracks.get(src);
    if (!track) {
      track = new Audio(src);
      track.loop = true;
      track.preload = 'auto';
      track.volume = 0;
      this.tracks.set(src, track);
    }
    return track;
  }
}

export const ambience = new Ambience();

export const soundBoard = new SoundBoard();
